import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Calendar, MapPin, Car } from 'lucide-react';

const WhatsAppButton = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    carModel: '',
    package: '',
    location: '',
    date: '',
    time: '',
  });

  const content = {
    modalTitle: isAr ? 'حجز سريع' : 'Quick Booking',
    labels: {
      name: isAr ? 'الاسم بالكامل' : 'Your Name',
      carModel: isAr ? 'موديل السيارة' : 'Car Model',
      package: isAr ? 'اختر الباقة' : 'Package',
      location: isAr ? 'العنوان في الكويت' : 'Location',
      date: isAr ? 'التاريخ' : 'Date',
      time: isAr ? 'الوقت' : 'Time',
    },
    placeholders: {
      name: isAr ? 'أحمد محمد' : 'John Doe',
      carModel: isAr ? 'تويوتا لاند كروزر' : 'Toyota Land Cruiser',
      location: isAr ? 'منطقتك، القطعة، الشارع' : 'Area, Block, Street',
      selectPkg: isAr ? 'اختر الباقة المناسبة' : 'Select a package',
    },
    btns: {
      book: isAr ? 'احجز الآن' : 'Book Now',
      quick: isAr ? 'رسالة سريعة' : 'Quick Message',
    },
    packages: [
      { value: 'Special', label: isAr ? 'خاصة (٥-٧ د.ك)' : 'Special (KD 5-7)' },
      { value: 'VIP', label: isAr ? 'VIP (١٠-١٢ د.ك)' : 'VIP (KD 10-12)' },
      {
        value: 'Royal',
        label: isAr ? 'ملكية (١٥-٢٠ د.ك)' : 'Royal (KD 15-20)',
      },
    ],
  };

  const openWhatsApp = (customMessage = '') => {
    let message = isAr
      ? 'مرحباً سوبر كلاسيكي، أود حجز خدمة غسيل سيارات.'
      : 'Hi Super Classy, I want to book a car wash service.';

    if (customMessage) {
      message = customMessage;
    } else if (formData.name) {
      message = isAr
        ? `طلب حجز جديد من الموقع (superclassykw):%0A%0Aالاسم: ${formData.name}%0Aموديل السيارة: ${formData.carModel}%0Aالباقة: ${formData.package}%0Aالعنوان: ${formData.location}%0Aالتاريخ: ${formData.date}%0Aالوقت: ${formData.time}`
        : `New Booking Request (superclassykw):%0A%0AName: ${formData.name}%0ACar Model: ${formData.carModel}%0APackage: ${formData.package}%0ALocation: ${formData.location}%0ADate: ${formData.date}%0ATime: ${formData.time}`;
    }

    window.open(`https://wa.me/96550040026?text=${message}`, '_blank');
    setIsFormOpen(false);
    setFormData({
      name: '',
      carModel: '',
      package: '',
      location: '',
      date: '',
      time: '',
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    openWhatsApp();
  };

  return (
    <>
      {/* WhatsApp Floating Button - Repositioned for RTL */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.1, rotate: 5 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsFormOpen(true)}
        className={`fixed bottom-6 ${isAr ? 'left-6' : 'right-6'} z-50 p-4 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-full shadow-[0_10px_30px_rgba(34,197,94,0.4)] hover:shadow-green-500/50 transition-all duration-300`}
      >
        <MessageCircle className='h-8 w-8' />
        <span className='absolute -top-1 -right-1 flex h-4 w-4'>
          <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75'></span>
          <span className='relative inline-flex rounded-full h-4 w-4 bg-green-400 border-2 border-white'></span>
        </span>
      </motion.button>

      {/* Quick Book Popup */}
      <AnimatePresence>
        {isFormOpen && (
          <div
            className='fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm'
            dir={isAr ? 'rtl' : 'ltr'}
          >
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              className='glass-card max-w-md w-full p-8 shadow-2xl border border-white/20'
            >
              <div className='flex items-center justify-between mb-8'>
                <h3 className='text-2xl font-black text-gradient'>
                  {content.modalTitle}
                </h3>
                <button
                  onClick={() => setIsFormOpen(false)}
                  className='p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors'
                >
                  <X className='h-6 w-6 text-gray-500' />
                </button>
              </div>

              <form onSubmit={handleSubmit} className='space-y-5'>
                <div className='space-y-1'>
                  <label className='text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-2'>
                    {content.labels.name}
                  </label>
                  <input
                    type='text'
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className='w-full px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-royal-purple-500 outline-none transition-all'
                    placeholder={content.placeholders.name}
                    required
                  />
                </div>

                <div className='space-y-1'>
                  <label className='text-xs font-bold uppercase tracking-widest text-gray-400'>
                    {content.labels.carModel}
                  </label>
                  <input
                    type='text'
                    value={formData.carModel}
                    onChange={(e) =>
                      setFormData({ ...formData, carModel: e.target.value })
                    }
                    className='w-full px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-royal-purple-500 outline-none transition-all'
                    placeholder={content.placeholders.carModel}
                    required
                  />
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-1'>
                    <label className='text-xs font-bold uppercase tracking-widest text-gray-400'>
                      {content.labels.package}
                    </label>
                    <select
                      value={formData.package}
                      onChange={(e) =>
                        setFormData({ ...formData, package: e.target.value })
                      }
                      className='w-full px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-royal-purple-500 outline-none transition-all'
                      required
                    >
                      <option value=''>{content.placeholders.selectPkg}</option>
                      {content.packages.map((pkg) => (
                        <option key={pkg.value} value={pkg.value}>
                          {pkg.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='space-y-1'>
                    <label className='text-xs font-bold uppercase tracking-widest text-gray-400'>
                      {content.labels.location}
                    </label>
                    <input
                      type='text'
                      value={formData.location}
                      onChange={(e) =>
                        setFormData({ ...formData, location: e.target.value })
                      }
                      className='w-full px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-royal-purple-500 outline-none transition-all'
                      placeholder={content.placeholders.location}
                      required
                    />
                  </div>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-1'>
                    <label className='text-xs font-bold uppercase tracking-widest text-gray-400'>
                      {content.labels.date}
                    </label>
                    <input
                      type='date'
                      value={formData.date}
                      onChange={(e) =>
                        setFormData({ ...formData, date: e.target.value })
                      }
                      className='w-full px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-royal-purple-500 outline-none'
                      required
                    />
                  </div>
                  <div className='space-y-1'>
                    <label className='text-xs font-bold uppercase tracking-widest text-gray-400'>
                      {content.labels.time}
                    </label>
                    <input
                      type='time'
                      value={formData.time}
                      onChange={(e) =>
                        setFormData({ ...formData, time: e.target.value })
                      }
                      className='w-full px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-royal-purple-500 outline-none'
                      required
                    />
                  </div>
                </div>

                <div className='flex gap-3 pt-4'>
                  <button
                    type='submit'
                    className='flex-1 py-4 premium-gradient text-white rounded-2xl font-black shadow-lg hover:shadow-royal-purple-500/30 transition-all flex items-center justify-center gap-2'
                  >
                    <Send className='h-5 w-5' />
                    {content.btns.book}
                  </button>
                  <button
                    type='button'
                    onClick={() => openWhatsApp()}
                    className='px-4 py-4 bg-green-500 hover:bg-green-600 text-white rounded-2xl font-black shadow-lg transition-all'
                    title={content.btns.quick}
                  >
                    <MessageCircle className='h-6 w-6' />
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default WhatsAppButton;
